import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel, ButtonSize } from '@vizit/atoms';
import { FormDialog, FormTextField } from '@vizit/forms';
import { PlusIcon } from '@vizit/icons';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { FormProductCategoryComboBox, type FormProductCategoryComboBoxProps } from '../form-product-category-combo-box';
import { ImageTypeField } from '../image-type-field';

const IMAGE_TYPE_OPTIONS = [
  { id: 'hero', name: 'Hero' },
  { id: 'carousel', name: 'Carousel' },
] as const;

type CollectionsFormSchema = {
  name: string;
  category: string;
  imageType: 'hero' | 'carousel';
};

interface CollectionsDialogProps {
  /**
   * Error message from submission attempt
   *
   * @optional
   * @type {string | null}
   */
  submissionErrorMessage?: string | null;
  /**
   * Query props for the product category combo box
   */
  productCategoryComboBoxProps: Omit<
    FormProductCategoryComboBoxProps<CollectionsFormSchema>,
    'control' | 'name' | 'label' | 'isRequired'
  >;
  /**
   * Default values for the form
   *
   * @optional
   * @type {CollectionsFormSchema}
   */
  defaultValues?: CollectionsFormSchema;
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * The text for the trigger button
   *
   * @optional
   * @type {string}
   */
  triggerButtonText?: string;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * When true, the "Hero" image type is omitted from the image-type picker, leaving only "Carousel".
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  hideHeroOption?: boolean;
  /**
   * Callback function to handle the collection creation
   */
  onSubmit: (data: CollectionsFormSchema) => Promise<void>;
  /**
   * Callback function when dialog open state changes
   *
   * @optional
   * @param isOpen - Whether the dialog is open
   */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Dialog component for creating a new collection
 * Allows users to enter collection details including name, product category, and image type
 *
 * @param props - The component props
 * @param props.onSubmit - Callback function when form is submitted
 *
 * @example
 * <CollectionsDialog onSubmit={(data) => console.log(data)} />
 */
function CollectionsDialog(props: CollectionsDialogProps) {
  const {
    submissionErrorMessage = null,
    productCategoryComboBoxProps,
    defaultValues = {
      name: '',
      category: '',
      imageType: undefined as unknown as 'hero' | 'carousel',
    },
    isOpen,
    hideTrigger = false,
    triggerButtonText = 'Create collection',
    useIcon = false,
    hideHeroOption = false,
    onSubmit,
    onOpenChange,
  } = props;

  const imageTypeOptions = hideHeroOption
    ? IMAGE_TYPE_OPTIONS.filter((option) => option.id !== 'hero')
    : IMAGE_TYPE_OPTIONS;

  const formSchema = yup.object().shape({
    name: yup.string().required('Collection name is required'),
    category: yup
      .string()
      .oneOf(
        productCategoryComboBoxProps.items.map((item) => item.id),
        'Product category is required',
      )
      .required('Product category is required'),
    imageType: yup
      .string()
      .oneOf(imageTypeOptions.map((option) => option.id))
      .required('Image type is required'),
  });

  const formMethods = useForm<CollectionsFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues,
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, reset, setError, clearErrors } = formMethods;

  const handleOnResetForm = () => {
    clearErrors();

    reset(defaultValues);
  };

  const handleFormSubmit = async (data: CollectionsFormSchema) => {
    await onSubmit(data);
  };

  React.useEffect(() => {
    if (submissionErrorMessage) {
      setError('name', { message: submissionErrorMessage });
    }
  }, [submissionErrorMessage, setError]);

  return (
    <FormDialog
      formMethods={formMethods}
      formName="collections"
      isOpen={isOpen}
      onSubmit={handleSubmit(handleFormSubmit)}
      onResetForm={handleOnResetForm}
      onOpenChange={onOpenChange}
      data-testid="collection-dialog"
    >
      <FormDialog.Trigger>
        {hideTrigger ? (
          <Button className="sr-only" aria-hidden="true" isDisabled>
            Open dialog
          </Button>
        ) : (
          <Button
            level={ButtonLevel.Primary}
            size={ButtonSize.Lg}
            aria-label={triggerButtonText}
            iconStart={useIcon ? undefined : <PlusIcon size={20} />}
            data-testid="collection-trigger"
          >
            {useIcon ? <PlusIcon size={20} /> : triggerButtonText}
          </Button>
        )}
      </FormDialog.Trigger>
      <FormDialog.Title>Create a collection</FormDialog.Title>
      <FormDialog.Subtitle>
        Name your collection and choose product category to benchmark your assets.
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        <div className="flex flex-col gap-10">
          <FormTextField
            control={control}
            name="name"
            label="Collection name"
            placeholder="Enter collection name"
            isRequired
            className="min-h-0"
            inputProps={{ 'data-testid': 'collection-name-input' }}
          />
          <FormProductCategoryComboBox
            control={control}
            name="category"
            label="Product category"
            isRequired
            menuTrigger="input"
            className="min-h-0"
            data-testid="collection-category"
            {...productCategoryComboBoxProps}
          />
          <ImageTypeField control={control} name="imageType" hideHeroOption={hideHeroOption} />
        </div>
      </FormDialog.FormFields>
      <FormDialog.SubmitButton data-testid="collection-submit">Create collection</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { CollectionsDialog };
export type { CollectionsFormSchema };
